import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  AutoAttendCancelModal,
  EndAutoAttendModal,
  MeetingHistory,
  meetingTitle,
} from '@copilot/ui';
import Loader from '../components/common/Loader';
import DeleteMeetingModal from '../components/meeting/DeleteMeetingModal';
import EndMeetingModal from '../components/meeting/EndMeetingModal';
import { LANGUAGES } from '../constants/languages';
import { useAutoAttendActions } from '../hooks/useAutoAttendActions';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useDeleteMeeting } from '../hooks/useDeleteMeeting';
import { useEndMeeting } from '../hooks/useEndMeeting';
import * as meetingApi from '../services/meetingApi';
import { getUserFriendlyError } from '../utils/errors';

const PAGE_SIZE = 10;

// The Meetings page UI is shared with Desktop (@copilot/ui's
// MeetingHistory). This page owns loading the list and ending/deleting
// meetings through Web's API client.
function MeetingHistoryPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [range, setRange] = useState('');
  const [page, setPage] = useState(1);
  const [meetings, setMeetings] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Silent re-read with the CURRENT search/range/page (never resets them),
  // used by auto-refresh. No loading flag or error state; the last good
  // list stays on failure.
  const refreshMeetings = useCallback(
    async ({ isStale }) => {
      const data = await meetingApi.listMeetings({
        search: search.trim() || undefined,
        range: range || undefined,
        page,
        pageSize: PAGE_SIZE,
      });

      if (!isStale()) {
        setMeetings(data.items);
        setTotalPages(data.total_pages);
        setTotal(data.total);
      }
    },
    [search, range, page]
  );

  useAutoRefresh(refreshMeetings);

  // End Meeting for a meeting that is still active - the same confirm-
  // then-end flow the details page uses (hooks/useEndMeeting.js). The
  // row is replaced with what the backend now says, so an ended meeting
  // loses its badge and End button and a genuine failure keeps both.
  const { endTarget, endingId, isEnding, endError, requestEnd, cancelEnd, confirmEnd } =
    useEndMeeting({
      onSettled: ({ ended, meeting: fresh, target }) => {
        setMeetings((current) =>
          current.map((row) => {
            if (row.id !== target.id) return row;
            if (fresh) return fresh;
            return ended ? { ...row, status: 'ended' } : row;
          })
        );
      },
    });

  // Auto Attend meetings are not ended with the PATCH above (the backend
  // refuses to set a bot meeting's status): a recording bot is asked to
  // leave (End Meeting) and a bot that has not started recording is
  // cancelled - hooks/useAutoAttendActions.js. The row is replaced with
  // what the backend now says.
  const {
    stopTarget,
    cancelTarget,
    requestStop,
    requestCancel,
    closeStop,
    closeCancel,
    confirmStop,
    confirmCancel,
    busyId: autoAttendBusyId,
    isStopping,
    isCancelling,
    actionError: autoAttendError,
    leavingIds,
  } = useAutoAttendActions({
    onSettled: ({ meeting: fresh, target }) => {
      if (!fresh) return;
      setMeetings((current) => current.map((row) => (row.id === target.id ? fresh : row)));
    },
  });

  // Delete permanently - hooks/useDeleteMeeting.js. A 404 counts as
  // "already gone" (another tab or Desktop deleted it first); a genuine
  // failure leaves the row as it was, with deleteError shown.
  const {
    deleteTarget,
    deletingId,
    isDeleting,
    deleteError,
    requestDelete,
    cancelDelete,
    confirmDelete,
  } = useDeleteMeeting({
    onSettled: ({ deleted, target }) => {
      if (!deleted) return;
      setMeetings((current) => current.filter((row) => row.id !== target.id));
      setTotal((current) => Math.max(0, current - 1));
    },
  });

  // A changed search or range goes back to page 1.
  useEffect(() => {
    setPage(1);
  }, [search, range]);

  useEffect(() => {
    let cancelled = false;

    const handle = setTimeout(
      async () => {
        setIsLoading(true);
        setError('');

        try {
          const data = await meetingApi.listMeetings({
            search: search.trim() || undefined,
            range: range || undefined,
            page,
            pageSize: PAGE_SIZE,
          });

          if (!cancelled) {
            setMeetings(data.items);
            setTotalPages(data.total_pages);
            setTotal(data.total);
          }
        } catch (err) {
          if (!cancelled) {
            setError(getUserFriendlyError(err, 'Unable to load meetings. Please try again.'));
          }
        } finally {
          if (!cancelled) {
            setIsLoading(false);
          }
        }
      },
      search ? 300 : 0
    );

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search, range, page]);

  return (
    <>
      <MeetingHistory
        meetings={meetings}
        isLoading={isLoading}
        error={error}
        loadingIndicator={<Loader label="Loading meetings..." />}
        search={search}
        onSearchChange={setSearch}
        range={range}
        onRangeChange={setRange}
        page={page}
        totalPages={totalPages}
        total={total}
        onPageChange={setPage}
        languages={LANGUAGES}
        onNewMeeting={() => navigate('/app/meeting/new')}
        meetingHref={(meeting) => `/app/meeting/${meeting.id}`}
        renderLink={({ href, className, ariaLabel, children }) => (
          <Link to={href} className={className} aria-label={ariaLabel}>
            {children}
          </Link>
        )}
        onEndMeeting={(meeting) => requestEnd({ id: meeting.id, title: meetingTitle(meeting) })}
        onEndAutoAttend={(meeting) => requestStop({ id: meeting.id, title: meetingTitle(meeting) })}
        onCancelAutoAttend={(meeting) =>
          requestCancel({ id: meeting.id, title: meetingTitle(meeting) })
        }
        endingId={endingId || autoAttendBusyId}
        leavingIds={leavingIds}
        actionError={endError || autoAttendError}
        onDeleteMeeting={(meeting) => requestDelete({ id: meeting.id, title: meetingTitle(meeting) })}
        deletingId={deletingId}
      />

      {endTarget && (
        <EndMeetingModal
          onCancel={cancelEnd}
          onConfirm={confirmEnd}
          isSubmitting={isEnding}
          cancelLabel="Cancel"
        />
      )}

      {stopTarget && (
        <EndAutoAttendModal
          onCancel={closeStop}
          onConfirm={confirmStop}
          isSubmitting={isStopping}
        />
      )}

      {cancelTarget && (
        <AutoAttendCancelModal
          onKeep={closeCancel}
          onConfirm={confirmCancel}
          isSubmitting={isCancelling}
        />
      )}

      {deleteTarget && (
        <DeleteMeetingModal
          onCancel={cancelDelete}
          onConfirm={confirmDelete}
          isSubmitting={isDeleting}
          error={deleteError}
        />
      )}
    </>
  );
}

export default MeetingHistoryPage;
